import { useMelwaysInfo } from '@seek/melways-react';
import type { Language } from '@seek/melways-sites';
import { useTranslations } from '@vocab/react';
import {
  IconPositive,
  HiddenVisually,
  Stack,
  Text,
  Columns,
  Column,
  Box,
  IconRenderer,
} from 'braid-design-system';

import { useAppConfig } from 'src/config/context';
import type { AppliedJobEvent } from 'src/types/graphql';
import { getEventLabel, getEventDesc } from 'src/utils';
import { getScreenReaderDate } from 'src/wip_modules/util/date';

import translations from '../MetaDataApplied/MetaDataApplied.vocab';

import * as styles from './ApplicationStatus.css';

interface Props {
  events: AppliedJobEvent[];
}

export const ApplicationStatusList = ({ events }: Props) => {
  const { t } = useTranslations(translations);
  const { language } = useMelwaysInfo();
  const { BRAND: brand } = useAppConfig();

  return (
    <Stack space="medium">
      {events.map((event, index) => {
        const isLast = index === events.length - 1;
        const eventLabel = getEventLabel(event.status, t, brand);
        const eventDesc = getEventDesc(event.status, t);

        return (
          <Box position="relative" key={index}>
            <Columns space="xxsmall">
              <Column width="content">
                <Box position="relative" height="full">
                  <Text
                    tone={
                      event.status === 'NotSuitable' ? 'secondary' : 'positive'
                    }
                  >
                    {event.status === 'NotSuitable' ? (
                      <IconRenderer>
                        {({ className }) => (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            className={className}
                            fill="currentColor"
                          >
                            <path d="M12 23C5.935 23 1 18.065 1 12S5.935 1 12 1s11 4.935 11 11-4.935 11-11 11Zm0-20c-4.963 0-9 4.038-9 9 0 4.963 4.037 9 9 9s9-4.037 9-9c0-4.962-4.037-9-9-9Z" />
                            <path d="M16 13H8a1 1 0 1 1 0-2h8a1 1 0 1 1 0 2Z" />
                          </svg>
                        )}
                      </IconRenderer>
                    ) : (
                      <IconPositive />
                    )}
                  </Text>
                  {!isLast ? (
                    <Box
                      component="span"
                      display="block"
                      position="absolute"
                      borderRadius="full"
                      marginTop="xsmall"
                      height="full"
                      left={0}
                      className={styles.connector}
                    />
                  ) : null}
                </Box>
              </Column>
              <Column>
                <Stack space="small">
                  <Text
                    tone={
                      event.status === 'NotSuitable' ? 'secondary' : 'positive'
                    }
                  >
                    {eventLabel}
                  </Text>
                  <Text tone="secondary" size="small">
                    {`${event.timestamp.shortAbsoluteLabel} `}
                    {event.timestamp.dateTimeUtc ? (
                      <HiddenVisually>
                        {getScreenReaderDate(
                          event.timestamp.dateTimeUtc,
                          language as Language,
                        )}
                      </HiddenVisually>
                    ) : null}
                  </Text>
                  {isLast ? <Text tone="secondary">{eventDesc}</Text> : null}
                </Stack>
              </Column>
            </Columns>
          </Box>
        );
      })}
    </Stack>
  );
};
